"use client";

import React from "react";
import { ArrowRight, ShieldCheck, Sun, House as Home, FolderKanban, FileText, PhoneCall } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const relatedItems = [{
  title: "Roofing scaffolding",
  description: "Full perimeter edge protection, chimney stacks, and temporary roofs engineered for re-roofing and maintenance teams.",
  to: "/RoofingScaffoldingPage",
  badge: "Specialist access",
  icon: "Home",
  photoKey: "Edge Protection Related Roofing Image",
  defaultImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffold_service.png",
  defaultSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffold_service.png-wvc-srcset",
  defaultSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/roofing_scaffold_service.png-wvc-sizes",
  defaultAlt: "Roofing scaffolding",
  actionText: "View roofing access"
}, {
  title: "Solar scaffolding",
  description: "Designed access and guardrails tailored specifically for residential and commercial photovoltaic solar panel installations.",
  to: "/SolarScaffoldingPage",
  badge: "Renewables",
  icon: "Sun",
  photoKey: "Edge Protection Related Solar Image",
  defaultImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/solar_scaffold_service.webp",
  defaultSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/solar_scaffold_service.webp-wvc-srcset",
  defaultSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/solar_scaffold_service.webp-wvc-sizes",
  defaultAlt: "Solar scaffolding",
  actionText: "View solar access"
}, {
  title: "Safety & accreditations",
  description: "Strict safety leadership backed by verified NASC and CHAS industry credentials across every site.",
  to: "/HealthSafetyAccreditationsPage",
  badge: "Compliance",
  icon: "ShieldCheck",
  photoKey: "Edge Protection Related Safety Image",
  defaultImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/safety_accreditations_card.png",
  defaultSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/safety_accreditations_card.png-wvc-srcset",
  defaultSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/safety_accreditations_card.png-wvc-sizes",
  defaultAlt: "Safety & accreditations",
  actionText: "Check accreditations"
}, {
  title: "Projects & case studies",
  description: "Explore our recent scaffold handovers across Berkshire, Surrey, Hampshire, Buckinghamshire, and London.",
  to: "/ProjectsCaseStudiesPage",
  badge: "Portfolio",
  icon: "FolderKanban",
  photoKey: "Edge Protection Related Projects Image",
  defaultImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/projects_case_studies.png",
  defaultSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/projects_case_studies.png-wvc-srcset",
  defaultSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1793066/img/projects_case_studies.png-wvc-sizes",
  defaultAlt: "Projects & case studies",
  actionText: "Explore projects"
}];
function RelatedServicesConsumer() {
  const {
    posts,
    loading
  } = usePosts();
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  const renderIcon = (iconName: string) => {
    switch (iconName) {
      case "Home":
        return <Home className="size-5 text-primary" />;
      case "Sun":
        return <Sun className="size-5 text-primary" />;
      case "ShieldCheck":
        return <ShieldCheck className="size-5 text-primary" />;
      case "FolderKanban":
        return <FolderKanban className="size-5 text-primary" />;
      default:
        return <FileText className="size-5 text-primary" />;
    }
  };
  const resolveImage = (photoKey: string, defaultImage: string, defaultSrcset: string, defaultSizes: string, defaultAlt: string) => {
    const post = posts?.find(p => p.title?.toLowerCase() === photoKey.toLowerCase() || p.customFields?.photo_name?.toLowerCase() === photoKey.toLowerCase());
    if (post) {
      const imgUrl = post.customFields?.image || post.featuredImage;
      const altText = post.customFields?.alt_text || defaultAlt;
      if (imgUrl) {
        return {
          src: imgUrl,
          srcset: "",
          sizes: "",
          alt: altText
        };
      }
      return {
        src: defaultImage,
        srcset: defaultSrcset,
        sizes: defaultSizes,
        alt: altText
      };
    }
    return {
      src: defaultImage,
      srcset: defaultSrcset,
      sizes: defaultSizes,
      alt: defaultAlt
    };
  };
  return <section id="relatedservices" data-nav="light" className="bg-background py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 pb-12 border-b border-border">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold uppercase tracking-wider text-primary">
              Specialist access &amp; credentials
            </span>
            <h2 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
              Explore our other services and credentials
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground font-default">
              Find purpose-built access solutions for roofing, solar installations, and accredited commercial projects across the South East.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ContactRequestQuotePage" className="inline-flex items-center gap-2">
                <span>Request a quote</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Services & Pages Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {relatedItems.map((item, i) => {
          const photo = resolveImage(item.photoKey, item.defaultImage, item.defaultSrcset, item.defaultSizes, item.defaultAlt);
          return <motion.div key={item.to} variants={itemVariants} data-index={i}>
                <Card className="h-full flex flex-col justify-between overflow-hidden border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <div className="relative aspect-16/10 w-full overflow-hidden bg-muted">
                    {loading ? <div className="h-full w-full bg-muted animate-pulse" /> : <img src={photo.src} data-wvc-srcset={photo.srcset} data-wvc-sizes={photo.sizes} alt={photo.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 hover:scale-105" />}
                    <div className="absolute top-3 left-3">
                      <Badge variant="secondary" className="bg-background/90 backdrop-blur-xs font-medium text-foreground">
                        {item.badge}
                      </Badge>
                    </div>
                  </div>

                  <div className="flex flex-col flex-1">
                    <CardHeader className="pt-5 pb-2">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="p-1.5 rounded-md bg-accent text-accent-foreground">
                          {renderIcon(item.icon)}
                        </div>
                      </div>
                      <CardTitle className="text-lg font-bold text-foreground font-default">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="text-sm text-muted-foreground mt-2 line-clamp-3 font-default">
                        {item.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-2 pb-4 flex-1">
                      {/* Content spacer */}
                    </CardContent>

                    <CardFooter className="pt-0 pb-6 border-t border-border/50 mt-auto">
                      <Button asChild variant="ghost" size="sm" className="w-full justify-between px-2 text-primary hover:text-primary hover:bg-accent">
                        <Link to={item.to}>
                          <span>{item.actionText}</span>
                          <ArrowRight className="size-4" />
                        </Link>
                      </Button>
                    </CardFooter>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Quick Callout Strip */}
        <div className="mt-14 rounded-xl bg-accent p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6 border border-border">
          <div className="flex items-center gap-4 text-center sm:text-left">
            <div className="hidden sm:flex size-12 rounded-full bg-primary/10 items-center justify-center shrink-0">
              <PhoneCall className="size-6 text-primary" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-semibold text-foreground font-default">
                Need urgent access or standard scaffolding specifications?
              </h3>
              <p className="text-sm text-muted-foreground mt-0.5">
                Speak directly with Pete Wood and our operational team for reliable site surveys and turnkey access proposals.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <Button asChild variant="default" size="default" className="w-full sm:w-auto">
              <Link to="/ContactRequestQuotePage">
                <span>Request a quote</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}
export default function RelatedServices() {
  const wp_query_params = {
    post_type: "site_photo",
    per_page: 50
  };
  const wp_query = new WP_Query(wp_query_params);
  return <PostsProvider wp_query={{
    post_type: "site_photo",
    per_page: 50,
    status: ["publish"]
  }}>
      <RelatedServicesConsumer />
    </PostsProvider>;
}